import { useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { Modal } from 'react-bootstrap';
import RecaptchaComponent from '../../components/RecaptchaComponent';
import { authHeader } from '../../helpers/authHeader';
import { useSocialLogin } from '../../hooks/useSocialLogin';
import CardInform from '../../templates/CardInform';

// headingTag: the card owns the page's only <h1> on /sign-up, but when it's embedded in a
// landing page hero the page heading is already the <h1> — pass 'h2' there so the page keeps
// a single top-level heading.
// controlId: override when two instances share a page (e.g. hero card + mobile sheet) so
// the email input ids stay unique.
const TrialEmailForm = ({ onSuccess, headingTag: Heading = 'h1', controlId = 'trialEmail' }) => {

    const navigate = useNavigate();

    const formRef = useRef();
    const emailFieldRef = useRef();
    const formSubmitBtnRef = useRef();
    const formErrorMessageRef = useRef();

    const [showEmailExistsModal, setShowEmailExistsModal] = useState(false);

    // Consent gate: both sign-up routes are blocked until the visitor accepts the T&Cs
    // and confirms they're 18+; clicking either button without them shows why.
    const tsCsCheckRef = useRef();
    const ageCheckRef = useRef();
    const [tsCsAccepted, setTsCsAccepted] = useState(false);
    const [ageConfirmed, setAgeConfirmed] = useState(false);
    const [showConsentErrors, setShowConsentErrors] = useState(false);
    const consentsGiven = tsCsAccepted && ageConfirmed;

    const requireConsents = () => {
        if (consentsGiven) return true;
        setShowConsentErrors(true);
        (tsCsAccepted ? ageCheckRef : tsCsCheckRef).current?.focus();
        return false;
    };

    // Defer reCAPTCHA script load until the user touches the form, same as
    // FreemiumSignUpForm.js - mounting RecaptchaComponent is what injects the script.
    const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
    const armRecaptcha = useCallback(() => setRecaptchaLoaded(true), []);

    const { steppedSignUpFlowWithGoogle } = useSocialLogin();

    const recapchaV3Key = import.meta.env.VITE_RECAPCHA_V3_KEY;

    const generateRecaptchaToken = async (action) =>
        new Promise((resolve) => {
            window.grecaptcha.ready(
                () =>
                    void (async () => {
                        const token = await window.grecaptcha.execute(
                            recapchaV3Key,
                            { action: action },
                        );

                        resolve(token);
                    })(),
            );
        });

    const onToken = useCallback(async () => {

    }, []);

    const setFormSubmitting = useCallback((submitting) => {
        if (submitting) {
            formSubmitBtnRef.current.disabled = true;
            formSubmitBtnRef.current.getElementsByTagName('span')[1].classList.remove('d-none')
        } else {
            formSubmitBtnRef.current.disabled = false;
            formSubmitBtnRef.current.getElementsByTagName('span')[1].classList.add('d-none')
        }
    }, []);

    const showErrorMessage = (message) => {
        formErrorMessageRef.current?.classList.add('d-block');

        // Convert array to string if needed
        const messageText = Array.isArray(message) ? message.join(', ') : String(message);

        // Check if the error is about email already existing
        if (messageText && (messageText.includes('already exists') || messageText.includes('alternate address'))) {
            formErrorMessageRef.current.innerHTML = `
                    <span>${messageText} <a href="/login" class="text-neutral-gray-700 small text-center">Login</a></span>
            `;
        } else {
            formErrorMessageRef.current.innerText = messageText;
        }
    }

    const hideErrorMessage = () => {
        formErrorMessageRef.current?.classList.remove('d-block');
        formErrorMessageRef.current.innerHTML = '';
    }

    const isEmailExistsError = (message) => {
        const text = Array.isArray(message) ? message.join(', ') : String(message);
        return text.includes('already exists') || text.includes('alternate address');
    };

    const handleEmailExistsError = () => {
        emailFieldRef.current.value = '';
        formRef.current.classList.remove('was-validated');
        setFormSubmitting(false);
        setShowEmailExistsModal(true);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const form = event.currentTarget;

        // Validate the e-mail and the consent checkboxes together so every error shows
        // at once; focus lands on the first problem field (e-mail, then checkboxes).
        const emailValid = form.checkValidity();
        if (!emailValid) formRef.current.classList.add('was-validated');
        const consentsOk = requireConsents();
        if (!emailValid) emailFieldRef.current?.focus();
        if (!emailValid || !consentsOk) return;

        setFormSubmitting(true);
        hideErrorMessage();

        const segmentToken = await generateRecaptchaToken('segment');

        let segmentFormData = new FormData();
        segmentFormData.append('email', emailFieldRef.current?.value);
        segmentFormData.append('token', segmentToken);

        let resSegment = await fetch(`${import.meta.env.VITE_API_URL}/segment`, {
            method: 'post',
            headers: authHeader(),
            body: segmentFormData,
        });

        const { error } = await resSegment.json();

        if (error) {
            if (error.email) {
                if (isEmailExistsError(error.email)) {
                    handleEmailExistsError();
                    return;
                }
                showErrorMessage(error.email);
            } else if (error.token) {
                showErrorMessage(error.token);
            } else {
                showErrorMessage(Object.values(error));
            }

            setFormSubmitting(false);
            return;
        }

        formRef.current.classList.add('was-validated');
        setFormSubmitting(false);

        sessionStorage.setItem('trial_signup_email', emailFieldRef.current.value);
        // Read by TrialPasswordForm when it registers the account.
        sessionStorage.setItem('trial_signup_tc', 'true');

        onSuccess(emailFieldRef.current.value);
    };

    return (
        <div className="d-flex flex-column gap-3">
        
            <div>                
                <Heading className="h3 fw-extra-bold mb-2" style={{ letterSpacing: '-0.5px' }}>Start your free trial</Heading>
                <p className="small su-text-body mb-0">Free access for 7 days, then £9.99/month</p>
            </div>

            <Form noValidate onSubmit={handleSubmit} ref={formRef} onFocusCapture={armRecaptcha}>
                <Row className="mb-3">
                    <Form.Group controlId={controlId}>
                        <Form.Label className="mb-1">E-mail address</Form.Label>
                        <Form.Control
                            size="md"
                            name="E-mail"
                            className="dark form-control text-white form-control-lg bg-dark-mode-200 fw-normal"
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                            title="Please provide an e-mail address."
                            placeholder="E-mail address"
                            required
                            ref={emailFieldRef}
                        />
                        <Form.Control.Feedback type="invalid">
                            Please provide an e-mail address.
                        </Form.Control.Feedback>
                    </Form.Group>
                </Row>

                <Form.Group controlId={`${controlId}TsCs`} className="mb-3">
                    <Form.Check>
                        <Form.Check.Input
                            type="checkbox"
                            className="dont-highlight-valid"
                            ref={tsCsCheckRef}
                            checked={tsCsAccepted}
                            isInvalid={showConsentErrors && !tsCsAccepted}
                            onChange={(e) => setTsCsAccepted(e.target.checked)}
                        />
                        <Form.Check.Label className="ms-2 small fw-normal">
                            I agree to the <Link target="_blank" to="/terms-and-conditions">Terms and conditions</Link> of SportsKinetic.
                        </Form.Check.Label>
                       
                    </Form.Check>
                </Form.Group>

                <Form.Group controlId={`${controlId}Age`} className="mb-3">
                    <Form.Check>
                        <Form.Check.Input
                            type="checkbox"
                            className="dont-highlight-valid"
                            ref={ageCheckRef}
                            checked={ageConfirmed}
                            isInvalid={showConsentErrors && !ageConfirmed}
                            onChange={(e) => setAgeConfirmed(e.target.checked)}
                        />
                        <Form.Check.Label className="ms-2 small fw-normal">
                            I confirm I am 18 or over. SportsKinetic is not intended for anyone under 18.
                        </Form.Check.Label>
                      
                    </Form.Check>
                </Form.Group>

                <div ref={formErrorMessageRef} className="invalid-feedback"></div>

                <button type="submit"  ref={formSubmitBtnRef} className="btn-sk-primary rounded-4 text-center text-decoration-none mb-2 w-100"><span>Start Free 7-Day Trial &rarr;</span>
                    <span className="spinner-border spinner-border-sm d-none position-absolute top-50 start-50 text-white mx-n2 mt-n2" role="status" aria-hidden="true"></span>
                </button>
                <div className="d-flex justify-content-center mb-2">
                    <CardInform />
                </div>

                {recaptchaLoaded && (
                    <RecaptchaComponent action="segment" onToken={onToken} sitekey={recapchaV3Key} />
                )}
            </Form>

            <div className="su-divider-text my-1">
                <span>OR</span>
            </div>

            <button
                type="button"
                className="su-sso-btn btn w-100 rounded-5 py-3 bg-white text-black d-flex align-items-center justify-content-center gap-2 border-2"
                onClick={() => { if (requireConsents()) steppedSignUpFlowWithGoogle('/sign-up/plan'); }}
            >
                <svg width="20" height="20" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                <span className="small fw-medium">Sign up with Google</span>
            </button>

            <p className="small su-text-muted text-center mt-2 mb-0">
                Have an account? <Link to="/login" className="fw-semibold su-link-accent text-decoration-none thumb-sized-target">Login</Link>
            </p>

            <p className="small su-text-muted text-center mb-0">
                <span className="fw-semibold text-white">18+ only.</span> Please gamble responsibly.{' '}
                <a href="https://www.begambleaware.org" target="_blank" rel="noopener noreferrer" className="fw-semibold su-link-accent">BeGambleAware.org</a>
            </p>

            <Modal
                centered
                show={showEmailExistsModal}
                onHide={() => setShowEmailExistsModal(false)}
                contentClassName="rounded-4"
            >
                <Modal.Body className="px-4 py-5">
                    <div className="d-flex flex-column gap-3">
                        <div className="d-flex flex-column gap-2 text-white">
                            <p className="fw-semi-bold mb-0">Problem signing up</p>
                            <p className="small mb-0">We were unable to register you, this may be because you have already used this e-mail address</p>
                            <p className="small fw-semi-bold mb-0">Try signing up with a different e-mail or sign in.</p>
                        </div>
                        <div className="d-flex flex-column gap-3 align-items-center">
                            <button
                                type="button"
                                className="btn btn-primary w-100 fw-semi-bold py-2 rounded-3"
                                onClick={() => navigate('/login')}
                            >
                                Sign in
                            </button>
                            <button
                                type="button"
                                className="btn btn-link text-neutral-gray-600 small text-decoration-underline p-0"
                                onClick={() => setShowEmailExistsModal(false)}
                            >
                                Try another e-mail address
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default TrialEmailForm;
